import { FC } from 'react';
import { Divider, Flex, Stack, VStack } from '@chakra-ui/react';
import { fromUnixTime } from 'date-fns';

import { ProviderPlanId } from '__generated__/GQL';
import { formatAmount } from 'common/formatAmount';
import Accordion from 'components/_core/Accordion';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import ErrorView from 'components/ErrorView';
import Loader from 'components/Loader';
import Price from 'components/Price';
import { StepProps } from 'components/Stepper/types';
import { toMajorUnits, VAT_RATE } from 'helpers/amount';
import { formatDate } from 'helpers/date';
import * as Layout from 'layouts/StepperLayout';

import { useUpgradePlanInfo } from './hooks/useUpgradePlanInfo';
import locales from './locales';
import * as S from './styles';

interface UpgradeSummaryProps extends StepProps {
  providerPlanId: ProviderPlanId;
}

const FAQAccordion: FC = () => (
  <Accordion>
    <Accordion.Item title={locales.faq.subscription.question}>
      <Accordion.Text>{locales.faq.subscription.answer}</Accordion.Text>
    </Accordion.Item>
    <Accordion.Item title={locales.faq.downgrade.question}>
      <Accordion.Text>{locales.faq.downgrade.answer}</Accordion.Text>
    </Accordion.Item>
  </Accordion>
);

const UpgradeSummary: FC<UpgradeSummaryProps> = ({
  goNext,
  providerPlanId,
}) => {
  const { data, error, loading } = useUpgradePlanInfo(providerPlanId);

  if (error) {
    return <ErrorView />;
  }

  if (loading || !data) {
    return <Loader />;
  }

  const {
    currentPlanName,
    isCapitalDeposit,
    isShineStart,
    nextBalance,
    trialPeriodLength,
    upgradePlanInfo,
  } = data;

  const monthlyCostWithoutVAT = toMajorUnits(upgradePlanInfo.priceWithoutVAT);
  const formattedMonthlyCostWithoutVAT = formatAmount({
    forcePlusSign: false,
    value: monthlyCostWithoutVAT,
  });

  const nextBillingDate = formatDate(
    fromUnixTime(Number(upgradePlanInfo?.nextBillingDate)),
    'PPP',
  );

  const hasSpecialOffer = trialPeriodLength > 0;

  const upgradeCostWithoutVAT = Number(upgradePlanInfo.cost.toFixed(2));
  const upgradeCostWithVAT = upgradeCostWithoutVAT * VAT_RATE;
  const hasEnoughFundsToPayUpgradeCost =
    toMajorUnits(nextBalance) >= upgradeCostWithVAT;

  const isInFreeMonth = hasSpecialOffer && !isShineStart && !isCapitalDeposit;
  const ongoingOffer = isShineStart ? 'shineStart' : 'capitalDeposit';

  const adjustmentDetails = hasSpecialOffer
    ? locales.formatString(locales.adjustment.detailsSpecialOffer, {
        initialOffer: locales.offers[ongoingOffer],
        planName: upgradePlanInfo.planName,
        trialPeriodLength,
      })
    : locales.formatString(locales.adjustment.details, {
        planName: upgradePlanInfo.planName,
      });

  const monthlyWithdrawalDescription = hasSpecialOffer
    ? (locales.formatString(locales.monthlyWithdrawal.descriptionSpecialOffer, {
        monthlyPrice: formattedMonthlyCostWithoutVAT,
        nextBillingDate,
        trialPeriodLength,
      }) as string)
    : (locales.formatString(locales.monthlyWithdrawal.description, {
        monthlyPrice: formattedMonthlyCostWithoutVAT,
        nextBillingDate,
      }) as string);

  const currentlyHasNoExtraSeat =
    upgradePlanInfo.extraSeats.withCurrentPlan === 0;

  const extraSeatCostDifference =
    ((upgradePlanInfo.extraSeats.withNextPlan -
      upgradePlanInfo.extraSeats.withCurrentPlan) *
      upgradePlanInfo.extraSeats.seatCost) /
    100;

  const descriptionKey =
    upgradePlanInfo.extraSeats.withNextPlan === 0
      ? locales.accountAccessUpdateDescription
      : locales.accountAccessUpdateWithExtraSeatsRemainingDescription;

  const titleKey = currentlyHasNoExtraSeat
    ? locales.accountAccessTitle
    : locales.accountAccessUpdateTitle;

  return (
    <Layout.Root>
      <Layout.Body>
        <Layout.HeaderButtonsContainer>
          <BackButton />
        </Layout.HeaderButtonsContainer>

        <Layout.BodyScrollableSection>
          <S.Title>
            {locales.formatString(locales.title, {
              planName: upgradePlanInfo.planName,
            })}
          </S.Title>
          <S.Subtitle>{locales.pricesIndicatedWithoutVAT}</S.Subtitle>

          <SunshineCard padding="space-0">
            <Flex
              align="flex-start"
              gap="space-16"
              justify="space-between"
              padding="space-24"
            >
              <Stack gap="space-8">
                <Text variant="primary">{locales.monthlyCost.title}</Text>
                <Text size="sm">
                  {locales.formatString(locales.monthlyCost.description, {
                    amount: formattedMonthlyCostWithoutVAT,
                    date: nextBillingDate,
                  })}
                </Text>
              </Stack>
              <Price
                boxProps={{
                  flexShrink: '0',
                }}
                size={0}
                value={monthlyCostWithoutVAT}
              />
            </Flex>
            <Divider />
            <Flex
              align="flex-start"
              gap="space-16"
              justify="space-between"
              padding="space-24"
            >
              <Stack gap="space-8">
                <Text variant="primary">
                  {locales.formatString(titleKey, { extraSeatLimit: 15 })}
                </Text>
                {!currentlyHasNoExtraSeat ? (
                  <Text size="sm">
                    {locales.formatString(
                      upgradePlanInfo.extraSeats.withCurrentPlan > 1
                        ? descriptionKey.plural
                        : descriptionKey.singular,
                      {
                        currentExtraSeats:
                          upgradePlanInfo.extraSeats.withCurrentPlan,
                        currentPlanName,
                        includedAccesses:
                          upgradePlanInfo.extraSeats.withCurrentPlan -
                          upgradePlanInfo.extraSeats.withNextPlan,
                        nextPlanName: upgradePlanInfo.planName,
                      },
                    )}
                  </Text>
                ) : null}
              </Stack>
              {currentlyHasNoExtraSeat ? (
                <Text
                  display="inline-block"
                  fontWeight="weight-500"
                  variant="primary"
                >
                  {locales.included}
                </Text>
              ) : (
                <>
                  <Price
                    boxProps={{
                      flexShrink: '0',
                    }}
                    canceled={upgradePlanInfo.extraSeats.withNextPlan === 0}
                    monthly
                    size={0}
                    value={Math.abs(extraSeatCostDifference)}
                  />
                  <Text
                    display="inline-block"
                    fontWeight="weight-500"
                    variant="primary"
                  >
                    {locales.included}
                  </Text>
                </>
              )}
            </Flex>

            <Divider />
            {!isInFreeMonth ? (
              <>
                <Flex
                  align="flex-start"
                  gap="space-16"
                  justify="space-between"
                  padding="space-24"
                >
                  <Stack gap="space-8" justify="space-between" width="full">
                    <Text variant="primary">
                      {hasSpecialOffer
                        ? locales.adjustment.titleSpecialOffer
                        : locales.adjustment.title}
                    </Text>
                    <Text size="sm">{adjustmentDetails}</Text>
                  </Stack>
                  <Price
                    boxProps={{
                      flexShrink: '0',
                    }}
                    size={0}
                    value={upgradeCostWithoutVAT}
                  />
                </Flex>

                <Divider width="full" />
              </>
            ) : null}
            <Flex align="flex-start" justify="space-between" padding="space-24">
              <S.MastercardText>
                {upgradePlanInfo.nbOfPremiumCards === 1
                  ? locales.singlePremiumCard
                  : locales.formatString(locales.multiplePremiumCards, {
                      nbOfCards: upgradePlanInfo.nbOfPremiumCards,
                    })}
              </S.MastercardText>
              <Text
                display="inline-block"
                fontWeight="weight-500"
                variant="primary"
              >
                {locales.free}
              </Text>
            </Flex>
          </SunshineCard>

          <S.WarningBoxWrapper>
            <Callout hideIcon variant="info">
              <Callout.Title>{locales.monthlyWithdrawal.title}</Callout.Title>
              <Callout.Content>{monthlyWithdrawalDescription}</Callout.Content>
            </Callout>
          </S.WarningBoxWrapper>

          {!hasEnoughFundsToPayUpgradeCost ? (
            <S.WarningBoxWrapper marginTop="abs24">
              <Callout hideIcon variant="error">
                <Callout.Content>{locales.insufficientFunds}</Callout.Content>
              </Callout>
            </S.WarningBoxWrapper>
          ) : null}

          <VStack alignItems="stretch" marginTop="space-32" spacing={0}>
            <Text>{locales.question}</Text>
            <FAQAccordion />
          </VStack>
        </Layout.BodyScrollableSection>

        <Layout.FooterContainer>
          <Divider />

          <S.FooterButtonsContainer>
            <Button
              as="a"
              href="https://www.shine.fr/tarifs"
              icon="link"
              iconPosition="right"
              rel="noopener"
              target="_blank"
              variant="inline-primary"
            >
              {locales.formatString(locales.seeAllAdvantagesOfPlan, {
                planName: upgradePlanInfo.planName,
              })}
            </Button>
            <Button
              icon="arrow-right"
              isDisabled={!hasEnoughFundsToPayUpgradeCost}
              onClick={() => goNext()}
            >
              {locales.next}
            </Button>
          </S.FooterButtonsContainer>
        </Layout.FooterContainer>
      </Layout.Body>
    </Layout.Root>
  );
};

export default UpgradeSummary;
